import React, {useEffect, useMemo, useState} from 'react';
import {Shell} from 'components/Shell';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ReservationsState} from 'store/reservations/ReservationsState';
import {paginationProps, ServiceSegment, serviceSegments} from 'views/serviceActions/utils/ServicesUtils';
import {Button, Calendar, Segmented, Space, Table, Tag, theme} from 'antd';
import {PlusOutlined, CloseOutlined} from '@ant-design/icons';
import {SorterResult} from 'antd/es/table/interface';
import classNames from 'classnames';
import {AppRoutePaths} from 'routing/Routes';
import {appState} from 'store/AppStore';
import {ReservationDto} from '../../../../../PRMGApiClient';
import {reservationsColumns} from './utils/ReservationsTableColumns';
import {observer} from 'mobx-react-lite';
import {CalendarCell} from './utils/ReservationsUtils';
import {CalendarView} from 'views/calendar/CalendarView';

export const ReservationListView: React.FC = observer(() => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [state] = useState(() => new ReservationsState());
  const [searchParams, setSearchParams] = useSearchParams();
  const [segment, setSegment] = useState<ServiceSegment>(ServiceSegment.Table);

  const {token} = theme.useToken();

  const data = useMemo(() => state.items, [state.items]);

  const columns = useMemo(() => {
    for (const column of reservationsColumns) {
      if (column.key === 'car') {
        column.filters = appState.carBrandModelsTree;
      }
      if (column.key === 'customer') {
        column.filters = [];
        for (const {customer} of data) {
          if (customer) {
            if (!column.filters?.find((filter) => filter.value === customer.customerId)) {
              column.filters.push({
                text: `${customer.firstName} ${customer.lastName}`,
                value: customer.customerId ?? '',
              });
            }
          }
        }
      }
    }

    return reservationsColumns;
  }, [data]);

  useEffect(() => {
    if (segment === ServiceSegment.Table) state.loadTableItems();
    else if (segment === ServiceSegment.Calendar) state.loadCalendarItems();
  }, [state, segment]);

  useEffect(() => {
    const vehicleId = searchParams.get('vehicleId');
    if (vehicleId) state.setFiltersVehicle(Number(vehicleId));
  }, [searchParams, setSearchParams, state]);

  return (
    <Shell
      title={t('Reservations')}
      actionButtons={[
        <Button
          type="primary"
          key="add"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate(AppRoutePaths.reservationCreate.url)}
          className="shell-btn"
        >
          {t('Add Reservation')}
        </Button>,
      ]}
    >
      <Space direction="vertical" className="w-full">
        <Space className={classNames('flex items-end', state.filtersCarId ? 'justify-between' : 'justify-end')}>
          {state.filtersCarId && (
            <Tag
              color="volcano"
              closable
              closeIcon={<CloseOutlined style={{color: token['volcano-7']}} />}
              className="h-max"
              onClose={() => {
                searchParams.delete('vehicleId');
                setSearchParams(searchParams);
                state.setFiltersVehicle();
              }}
            >
              {state.filtersCarName}
            </Tag>
          )}
          <Segmented options={serviceSegments} onChange={(value) => setSegment(value as ServiceSegment)} />
        </Space>
        {segment === ServiceSegment.Table && (
          <div className="overflow-scroll">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={data}
              loading={appState.isLoading}
              pagination={{
                ...paginationProps,
                current: state.pageNumber,
                total: state.total,
                pageSize: state.pageSize,
              }}
              onChange={(pagination, filters, sorters) => {
                state.setPagination(pagination);
                state.setFilters(filters);
                state.setSorters(sorters as SorterResult<ReservationDto>);
              }}
            />
          </div>
        )}
        {segment === ServiceSegment.Calendar && (
          <Calendar
            dateCellRender={(value) => <CalendarCell date={value.toDate()} items={state.items} type="day" />}
            monthCellRender={(value) => <CalendarCell date={value.toDate()} items={state.items} type="month" />}
            onPanelChange={(value, mode) => state.setFiltersCalendar(value.toDate(), mode)}
          />
        )}
        {segment === ServiceSegment.Gantt && <CalendarView />}
      </Space>
    </Shell>
  );
});
